import { useEffect, useState } from 'react';

import { FiltersButton, FiltersDrawer } from '@/components/ListFilters';
import { InputDateRange } from '@/design_system/InputDate';
import { InputDateRangeValue } from '@/design_system/InputDate/InputDateRange';
import Stack from '@/design_system/Stack';
import { ErrorBoundary } from '@/services/sentry';
import useViewPort from '@/utils/useViewport';

import { InvoiceLineDestinationSelect } from './filters/InvoiceLineDestinationSelect';
import { InvoiceLineOriginSelect } from './filters/InvoiceLineOriginSelect';
import {
  InvoiceLinePaymentStrategy,
  InvoiceLinePaymentStrategySelect,
} from './filters/InvoiceLinePaymentStrategySelect';
import { InvoiceLineSearch } from './filters/InvoiceLineSearch';

export const InvoiceLinesFilter = ({
  setPage,
  search,
  debouncedSetSearch,
  invoiceLineOrigin,
  setInvoiceLineOrigin,
  invoiceLineDestination,
  setInvoiceLineDestination,
  invoiceLinePaymentStrategy,
  setInvoiceLinePaymentStrategy,
  dateRange,
  setDateRange,
}: {
  setPage: (page: number) => void;
  search: string;
  debouncedSetSearch: (value: string) => void;
  invoiceLineOrigin: string;
  setInvoiceLineOrigin: (origin: string) => void;
  invoiceLineDestination: string;
  setInvoiceLineDestination: (destination: string) => void;
  invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | '';
  setInvoiceLinePaymentStrategy: (
    invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | ''
  ) => void;
  dateRange: InputDateRangeValue;
  setDateRange: (dateRange: InputDateRangeValue) => void;
}) => {
  const { isMobile } = useViewPort();

  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const hasActiveFilters =
    invoiceLineOrigin !== '' ||
    invoiceLineDestination !== '' ||
    invoiceLinePaymentStrategy !== '' ||
    dateRange.option !== 'any';

  return (
    <ErrorBoundary>
      <Stack row gap="1rem">
        <InvoiceLineSearch search={search} debouncedSetSearch={debouncedSetSearch} />

        {!isMobile && (
          <>
            <InvoiceLineOriginSelect
              setPage={setPage}
              invoiceLineOrigin={invoiceLineOrigin}
              setInvoiceLineOrigin={setInvoiceLineOrigin}
              style={{ flex: 1, minWidth: '200px', maxWidth: '320px' }}
            />
            <InvoiceLineDestinationSelect
              setPage={setPage}
              invoiceLineDestination={invoiceLineDestination}
              setInvoiceLineDestination={setInvoiceLineDestination}
              style={{ flex: 1, minWidth: '200px', maxWidth: '320px' }}
            />
            <InvoiceLinePaymentStrategySelect
              setPage={setPage}
              invoiceLinePaymentStrategy={invoiceLinePaymentStrategy}
              setInvoiceLinePaymentStrategy={setInvoiceLinePaymentStrategy}
              style={{ flex: 1, minWidth: '200px', maxWidth: '320px' }}
            />
            <InputDateRange
              value={dateRange}
              onChange={(newRange) => {
                setDateRange(newRange);
                setPage(1);
              }}
              style={{ flex: 1, minWidth: '200px', maxWidth: '320px' }}
            />
          </>
        )}

        {isMobile && (
          <>
            <FiltersButton
              hasActiveFilters={hasActiveFilters}
              isFiltersDrawerOpen={isFiltersDrawerOpen}
              setIsFiltersDrawerOpen={setIsFiltersDrawerOpen}
            />
            <MobileFilters
              isOpen={isFiltersDrawerOpen}
              onOpenChange={setIsFiltersDrawerOpen}
              setPage={setPage}
              invoiceLineOrigin={invoiceLineOrigin}
              setInvoiceLineOrigin={setInvoiceLineOrigin}
              invoiceLineDestination={invoiceLineDestination}
              setInvoiceLineDestination={setInvoiceLineDestination}
              invoiceLinePaymentStrategy={invoiceLinePaymentStrategy}
              setInvoiceLinePaymentStrategy={setInvoiceLinePaymentStrategy}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </>
        )}
      </Stack>
    </ErrorBoundary>
  );
};

const MobileFilters = ({
  isOpen,
  onOpenChange,
  setPage,
  invoiceLineOrigin,
  setInvoiceLineOrigin,
  invoiceLineDestination,
  setInvoiceLineDestination,
  invoiceLinePaymentStrategy,
  setInvoiceLinePaymentStrategy,
  dateRange,
  setDateRange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  setPage: (page: number) => void;
  invoiceLineOrigin: string;
  setInvoiceLineOrigin: (origin: string) => void;
  invoiceLineDestination: string;
  setInvoiceLineDestination: (destination: string) => void;
  invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | '';
  setInvoiceLinePaymentStrategy: (
    invoiceLinePaymentStrategy: InvoiceLinePaymentStrategy | ''
  ) => void;
  dateRange: InputDateRangeValue;
  setDateRange: (dateRange: InputDateRangeValue) => void;
}) => {
  const [newInvoiceLineOrigin, setNewInvoiceLineOrigin] = useState(invoiceLineOrigin);
  const [newInvoiceLineDestination, setNewInvoiceLineDestination] =
    useState(invoiceLineDestination);
  const [newInvoiceLinePaymentStrategy, setNewInvoiceLinePaymentStrategy] = useState(
    invoiceLinePaymentStrategy
  );
  const [newDateRange, setNewDateRange] = useState(dateRange);

  const handleClearFilters = () => {
    setNewInvoiceLineOrigin('');
    setNewInvoiceLineDestination('');
    setNewInvoiceLinePaymentStrategy('');
    setNewDateRange({ option: 'any' });
  };

  const handleApplyFilters = () => {
    const hasInvoiceLineOriginBeenUpdated = invoiceLineOrigin !== newInvoiceLineOrigin;
    const hasInvoiceLineDestinationBeenUpdated =
      invoiceLineDestination !== newInvoiceLineDestination;
    const hasInvoiceLinePaymentStrategyBeenUpdated =
      invoiceLinePaymentStrategy !== newInvoiceLinePaymentStrategy;
    const hasDateRangeBeenUpdated =
      dateRange.option !== newDateRange.option ||
      dateRange.start !== newDateRange.start ||
      dateRange.end !== newDateRange.end;

    if (hasInvoiceLineOriginBeenUpdated) {
      setInvoiceLineOrigin(newInvoiceLineOrigin);
    }

    if (hasInvoiceLineDestinationBeenUpdated) {
      setInvoiceLineDestination(newInvoiceLineDestination);
    }

    if (hasInvoiceLinePaymentStrategyBeenUpdated) {
      setInvoiceLinePaymentStrategy(newInvoiceLinePaymentStrategy);
    }

    if (hasDateRangeBeenUpdated) {
      setDateRange(newDateRange);
    }

    if (
      hasInvoiceLineOriginBeenUpdated ||
      hasInvoiceLineDestinationBeenUpdated ||
      hasInvoiceLinePaymentStrategyBeenUpdated ||
      hasDateRangeBeenUpdated
    ) {
      setPage(1);
    }

    onOpenChange(false);
  };

  // Those useEffect aim to fill the local state of the drawer with the already applied filters
  useEffect(() => {
    setNewInvoiceLineOrigin(invoiceLineOrigin);
  }, [invoiceLineOrigin, isOpen]);

  useEffect(() => {
    setNewInvoiceLineDestination(invoiceLineDestination);
  }, [invoiceLineDestination, isOpen]);

  useEffect(() => {
    setNewInvoiceLinePaymentStrategy(invoiceLinePaymentStrategy);
  }, [invoiceLinePaymentStrategy, isOpen]);

  useEffect(() => {
    setNewDateRange(dateRange);
  }, [dateRange, isOpen]);

  return (
    <FiltersDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      handleClearFilters={handleClearFilters}
      handleApplyFilters={handleApplyFilters}
    >
      <InvoiceLineOriginSelect
        invoiceLineOrigin={newInvoiceLineOrigin}
        setInvoiceLineOrigin={setNewInvoiceLineOrigin}
        style={{ flex: 1 }}
      />
      <InvoiceLineDestinationSelect
        invoiceLineDestination={newInvoiceLineDestination}
        setInvoiceLineDestination={setNewInvoiceLineDestination}
        style={{ flex: 1 }}
      />
      <InvoiceLinePaymentStrategySelect
        invoiceLinePaymentStrategy={newInvoiceLinePaymentStrategy}
        setInvoiceLinePaymentStrategy={setNewInvoiceLinePaymentStrategy}
        style={{ flex: 1 }}
      />
      <InputDateRange value={newDateRange} onChange={setNewDateRange} style={{ flex: 1 }} />
    </FiltersDrawer>
  );
};
